import { formatInvalidCodeReasons } from "encamp-compliance-engine/src/schemas/rcra/wasteCodeMessages";
import { Control, Controller, FieldPathByValue, FieldValues } from "react-hook-form";

import { normalizeWasteCodeInput } from "../wasteActivitySelections";
import { WasteCodeChipInput } from "../WasteCodeChipInput";
import { InvalidWasteCode } from "./wasteCodeValidation";

/**
 * A repeatable, free-text list of waste codes bound to a react-hook-form
 * `string[]` field, rendered with the shared `WasteCodeChipInput`. Used for
 * both the §13 episodic federal/state code lists and the §16 HSM activity
 * codes. Entered codes are normalized (trimmed, uppercased, deduped) before
 * they land in the form — same as the Reportable Waste Codes step — so
 * ` d001 ` becomes a valid `D001` chip instead of a red one, and the same
 * code can't be added twice in different casings.
 *
 * When `getInvalidCodes` is provided, flagged codes render as red (error) chips
 * with the reasons as error helper text — the same UX as the Waste Activity
 * step's code fields. Validation is advisory: it never blocks the dialog's
 * save (the step's compliance-engine issues stay advisory too).
 */
export function WasteCodesField<T extends FieldValues>({
  name,
  label,
  disabled,
  control,
  getInvalidCodes,
}: {
  name: FieldPathByValue<T, string[]>;
  label: string;
  disabled: boolean;
  control: Control<T>;
  /** Live client-side validation; omitted → no validation (today's behavior). */
  getInvalidCodes?: (codes: string[]) => InvalidWasteCode[];
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const codes: string[] = field.value ?? [];
        const invalid = getInvalidCodes?.(codes) ?? [];

        return (
          <WasteCodeChipInput
            label={label}
            codes={codes}
            onChange={(next) => field.onChange(normalizeWasteCodeInput(next))}
            disabled={disabled}
            invalidCodes={invalid.map(({ code }) => code)}
            error={invalid.length > 0}
            helperText={invalid.length > 0 ? formatInvalidCodeReasons(invalid) : undefined}
          />
        );
      }}
    />
  );
}
